import React from "react";
import { Form, InputNumber, Checkbox, Select, Button } from "antd";
import styled from "styled-components";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

interface Option {
  label: string;
  value: string;
}

interface CommercialSpace {
  name: string | null;
  hsp: number | null;
  mezzanine: boolean;
  rdc_surface_min: number | null;
  rdc_surface_max: number | null;
  sous_sol: boolean;
  sous_sol_surface_min: number | null;
  sous_sol_surface_max: number | null;
  mezzanine_surface_min: number | null;
  mezzanine_surface_max: number | null;
}

interface Props {
  form: any;
  formData: any;
  setFormData: any;
}

const typeDeProjetOptions: Record<string, Option[]> = {
  "locaux commerciaux": [
    { label: "Centres commerciaux", value: "centres commerciaux" },
    { label: "Pied d'immeuble", value: "pied d'immeuble" },
    { label: "Showrooms", value: "showrooms" },
  ],
};

const CommercialsFormList: React.FC<Props> = ({
  form,
  formData,
  setFormData,
}) => {
  const handleSurfaceChange = (index: number, field: string, value: number | null) => {
    const currentPrices = form.getFieldValue("locaux_commerciaux_prices") || [];
    const locaux_commerciaux = form.getFieldValue("locaux_commerciaux") || [];
    const currentPrice = currentPrices[index] || {};
  
    if (index >= currentPrices.length) return;
  
    const updatedPrices = [...currentPrices];
  
    // Get the surfaces from the form values
    const rdcSurfaceMin = locaux_commerciaux[index]?.rdc_surface_min || 0;
    const rdcSurfaceMax = locaux_commerciaux[index]?.rdc_surface_max || 0;
    const mezzanineSurfaceMin = locaux_commerciaux[index]?.mezzanine_surface_min || 0;
    const mezzanineSurfaceMax = locaux_commerciaux[index]?.mezzanine_surface_max || 0;
    const sousSolSurfaceMin = locaux_commerciaux[index]?.sous_sol_surface_min || 0;
    const sousSolSurfaceMax = locaux_commerciaux[index]?.sous_sol_surface_max || 0;
  
    // Calculate the weighted surface
    const weightedSurfaceMin = (rdcSurfaceMin * 1.0) + (mezzanineSurfaceMin * 0.5) + (sousSolSurfaceMin * 0.3333);
    const weightedSurfaceMax = (rdcSurfaceMax * 1.0) + (mezzanineSurfaceMax * 0.5) + (sousSolSurfaceMax * 0.3333);
  
    // Update the total price based on the unit price
    if (currentPrice.prix_vente_unitaire_min) {
      updatedPrices[index] = {
        ...updatedPrices[index],
        prix_vente_min: Math.round(currentPrice.prix_vente_unitaire_min * weightedSurfaceMin),
      };
    }
  
    if (currentPrice.prix_vente_unitaire_max) {
      updatedPrices[index] = {
        ...updatedPrices[index],
        prix_vente_max: Math.round(currentPrice.prix_vente_unitaire_max * weightedSurfaceMax),
      };
    }
  
    form.setFieldsValue({ locaux_commerciaux_prices: updatedPrices });
    setFormData((prev: any) => ({
      ...prev,
      locaux_commerciaux_prices: updatedPrices,
    }));
  };
  return (
    <>
      <Form.List name="locaux_commerciaux">
        {(fields, { add, remove }) => {
          if (fields.length === 0) {
            add({
              name: null,
              hsp: null,
              mezzanine: false,
              rdc_surface_min: null,
              rdc_surface_max: null,
              sous_sol: false,
              sous_sol_surface_min: null,
              sous_sol_surface_max: null,
              mezzanine_surface_min: null,
              mezzanine_surface_max: null,
            });
          }
          return (
            <>
              <ScrollableTableWrapper>
                <table
                  style={{
                    minWidth: "1400px",
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          ...headerStyle,
                          width: "50px",
                          borderBottom: "none",
                          borderLeft: "none",
                          borderTop: "none",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Button
                          color="primary"
                          variant="solid"
                          onClick={() => {
                            add({
                              name: null,
                              hsp: null,
                              mezzanine: false,
                              rdc_surface_min: null,
                              rdc_surface_max: null,
                              sous_sol: false,
                              sous_sol_surface_min: null,
                              sous_sol_surface_max: null,
                              mezzanine_surface_min: null,
                              mezzanine_surface_max: null,
                            });
                          }}
                          icon={<PlusOutlined />}
                        />
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Type
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        HSP (m)
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Surface RDC (m²)
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Mezzanine
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Surface Mezzanine (m²)
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Sous-sol
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Surface Sous-sol (m²)
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          ...subHeaderStyle,
                          borderTop: "none",
                          backgroundColor: "#fff",
                        }}
                      ></th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map(({ key, name, ...restField }) => {
                      const currentValue = form.getFieldValue([
                        "locaux_commerciaux",
                        name,
                        "name",
                      ]);
                      return (
                        <tr key={key}>
                          <td style={cellStyle}>
                            <Button
                              variant="solid"
                              color="danger"
                              disabled={fields.length === 1}
                              onClick={() => {
                                remove(name);

                                // After removing apartment, update the prices array
                                setTimeout(() => {
                                  const currentApartments =
                                    form.getFieldValue("locaux_commerciaux") || [];
                                  const currentPrices =
                                    form.getFieldValue("locaux_commerciaux_prices") ||
                                    [];

                                  // Remove the corresponding price entry
                                  const newPrices = currentPrices.filter(
                                    (_:any, index:number) => index !== name
                                  );

                                  form.setFieldsValue({
                                    locaux_commerciaux_prices: newPrices,
                                  });
                                  setFormData((prev:any) => ({
                                    ...prev,
                                    locaux_commerciaux_prices: newPrices,
                                  }));
                                }, 0); // Use setTimeout to ensure the apartment is removed first
                              }}
                              icon={<MinusCircleOutlined />}
                            />
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "name"]}
                              rules={[
                                {
                                  required: true,message:""}
                              ]}
                            >
                              <Select
                                style={{ width: "100%" }}
                                placeholder="Select type"
                                value={currentValue}
                                options={typeDeProjetOptions[
                                  "locaux commerciaux"
                                ].map((option) => {
                                  const currentSpaces =
                                    form.getFieldValue("locaux_commerciaux") ||
                                    [];
                                  const isDisabled = currentSpaces.some(
                                    (s: CommercialSpace, idx: number) =>
                                      idx !== name && s.name === option.value
                                  );
                                  return { ...option, disabled: isDisabled };
                                })}
                                onChange={(value) => {
                                  if (!currentValue) {
                                    form.setFields([
                                      {
                                        name: [
                                          "locaux_commerciaux",
                                          name,
                                          "name",
                                        ],
                                        value,
                                      },
                                    ]);
                                  }
                                }}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "hsp"]}
                              rules={[{ required: true, message: "" }]}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                step={0.1}
                                min={0}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "rdc_surface_min"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "locaux_commerciaux",
                                      name,
                                      "rdc_surface_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber onChange={(value) => handleSurfaceChange(name, "rdc_surface_min", value)} min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "rdc_surface_max"]}
                              rules={[{ required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "locaux_commerciaux",
                                      name,
                                      "rdc_surface_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber onChange={(value) => handleSurfaceChange(name, "rdc_surface_max", value)} min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          </td>

                          {/* Mezzanine Checkbox */}
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "mezzanine"]}
                              valuePropName="checked"
                            >
                              <Checkbox
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  if (!checked) {
                                    // When unchecked, clear the mezzanine surface values
                                    const currentSpaces =
                                      form.getFieldValue("locaux_commerciaux") || [];
                                    const updatedSpaces = currentSpaces.map(
                                      (space: CommercialSpace, idx: number) =>
                                        idx === name
                                          ? {
                                              ...space,
                                              mezzanine_surface_min: null,
                                              mezzanine_surface_max: null,
                                            }
                                          : space
                                    );
                                    form.setFieldsValue({
                                      locaux_commerciaux: updatedSpaces,
                                    });
                                    setFormData({
                                      ...formData,
                                      locaux_commerciaux: updatedSpaces,
                                    });
                                  }
                                }}
                              />
                            </Form.Item>
                          </td>

                          {/* Mezzanine Surface Min */}
                          <td style={cellStyle}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) =>
                                prev.locaux_commerciaux?.[name]?.mezzanine !==
                                curr.locaux_commerciaux?.[name]?.mezzanine
                              }
                            >
                              {({ getFieldValue }) => {
                                const isMezzanine = getFieldValue([
                                  "locaux_commerciaux",
                                  name,
                                  "mezzanine",
                                ]);
                                return (
                                  <Form.Item
                                    style={{ marginBottom: 0 }}
                                    {...restField}
                                    name={[name, "mezzanine_surface_min"]}
                                    rules={[
                                      {
                                        required: isMezzanine,
                                        message: "",
                                      },
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const max = getFieldValue([
                                            "locaux_commerciaux",
                                            name,
                                            "mezzanine_surface_max",
                                          ]);
                                          if (!value || !max || value <= max) {
                                            return Promise.resolve();
                                          }
                                          return Promise.reject(new Error(""));
                                        },
                                      }),
                                    ]}
                                  >
                                    <InputNumber
                                    min={0}
                                      disabled={!isMezzanine}
                                      style={{ width: "100%" }}
                                      onChange={(value) => handleSurfaceChange(name, "mezzanine_surface_min", value)}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </td>

                          {/* Mezzanine Surface Max */}
                          <td style={cellStyle}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) =>
                                prev.locaux_commerciaux?.[name]?.mezzanine !==
                                curr.locaux_commerciaux?.[name]?.mezzanine
                              }
                            >
                              {({ getFieldValue }) => {
                                const isMezzanine = getFieldValue([
                                  "locaux_commerciaux",
                                  name,
                                  "mezzanine",
                                ]);
                                return (
                                  <Form.Item
                                    style={{ marginBottom: 0 }}
                                    {...restField}
                                    name={[name, "mezzanine_surface_max"]}
                                    rules={[
                                      {
                                        required: isMezzanine,
                                        message: "",
                                      },
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const min = getFieldValue([
                                            "locaux_commerciaux",
                                            name,
                                            "mezzanine_surface_min",
                                          ]);
                                          if (!value || !min || value >= min) {
                                            return Promise.resolve();
                                          }
                                          return Promise.reject(new Error(""));
                                        },
                                      }),
                                    ]}
                                  >
                                    <InputNumber
                                    min={0}
                                      disabled={!isMezzanine}
                                      style={{ width: "100%" }}
                                      onChange={(value) => handleSurfaceChange(name, "mezzanine_surface_max", value)}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </td>

                          {/* Sous-sol Checkbox */}
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "sous_sol"]}
                              valuePropName="checked"
                            >
                              <Checkbox
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  if (!checked) {
                                    // When unchecked, clear the sous-sol surface values
                                    const currentSpaces =
                                      form.getFieldValue("locaux_commerciaux") || [];
                                    const updatedSpaces = currentSpaces.map(
                                      (space: CommercialSpace, idx: number) =>
                                        idx === name
                                          ? {
                                              ...space,
                                              sous_sol_surface_min: null,
                                              sous_sol_surface_max: null,
                                            }
                                          : space
                                    );
                                    form.setFieldsValue({
                                      locaux_commerciaux: updatedSpaces,
                                    });
                                    setFormData({
                                      ...formData,
                                      locaux_commerciaux: updatedSpaces,
                                    });
                                  }
                                }}
                              />
                            </Form.Item>
                          </td>

                          {/* Sous-sol Surface Min */}
                          <td style={cellStyle}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) =>
                                prev.locaux_commerciaux?.[name]?.sous_sol !==
                                curr.locaux_commerciaux?.[name]?.sous_sol
                              }
                            >
                              {({ getFieldValue }) => {
                                const isSousSol = getFieldValue([
                                  "locaux_commerciaux",
                                  name,
                                  "sous_sol",
                                ]);
                                return (
                                  <Form.Item
                                    style={{ marginBottom: 0 }}
                                    {...restField}
                                    name={[name, "sous_sol_surface_min"]}
                                    rules={[
                                      {
                                        required: isSousSol,
                                        message: "",
                                      },
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const max = getFieldValue([
                                            "locaux_commerciaux",
                                            name,
                                            "sous_sol_surface_max",
                                          ]);
                                          if (!value || !max || value <= max) {
                                            return Promise.resolve();
                                          }
                                          return Promise.reject(new Error(""));
                                        },
                                      }),
                                    ]}
                                  >
                                    <InputNumber
                                      disabled={!isSousSol}
                                      min={0}
                                      style={{ width: "100%" }}
                                      onChange={(value) => handleSurfaceChange(name, "sous_sol_surface_min", value)}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </td>

                          {/* Sous-sol Surface Max */}
                          <td style={cellStyle}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) =>
                                prev.locaux_commerciaux?.[name]?.sous_sol !==
                                curr.locaux_commerciaux?.[name]?.sous_sol
                              }
                            >
                              {({ getFieldValue }) => {
                                const isSousSol = getFieldValue([
                                  "locaux_commerciaux",
                                  name,
                                  "sous_sol",
                                ]);
                                return (
                                  <Form.Item
                                    style={{ marginBottom: 0 }}
                                    {...restField}
                                    name={[name, "sous_sol_surface_max"]}
                                    rules={[
                                      {
                                        required: isSousSol,
                                        message: "",
                                      },
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          const min = getFieldValue([
                                            "locaux_commerciaux",
                                            name,
                                            "sous_sol_surface_min",
                                          ]);
                                          if (!value || !min || value >= min) {
                                            return Promise.resolve();
                                          }
                                          return Promise.reject(new Error(""));
                                        },
                                      }),
                                    ]}
                                  >
                                    <InputNumber
                                      disabled={!isSousSol}
                                      style={{ width: "100%" }}
                                      onChange={(value) => handleSurfaceChange(name, "sous_sol_surface_min", value)}
                                      min={0}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollableTableWrapper>
            </>
          );
        }}
      </Form.List>
    </>
  );
};

const headerStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  fontSize: "14px",
  fontWeight: "bold",
  color: "#42005a",
  backgroundColor: "#eeedf0",
  width: "150px",
};

const subHeaderStyle = {
  ...headerStyle,
  fontSize: "12px",
  borderLeft: "none",
};

const cellStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  textAlign: "center" as const,
};

const ScrollableTableWrapper = styled.div`
  overflow-x: auto;
  padding-bottom: 8px;
  margin-top: 16px;

  &::-webkit-scrollbar {
    height: 16px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #42005a;
    border-radius: 4px;
    &:hover {
      background-color: #603570;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: #f1f1f1;
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: #42005a #f1f1f1;
`;

export default CommercialsFormList;
